import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Phone, Globe, Instagram, Star, BadgeCheck, Pencil, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { fetchRestaurant, claimRestaurant, formatApiErrorDetail, trackRestaurantView } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ReviewSection from "@/components/ReviewSection";
import VideoSection from "@/components/VideoSection";
import SocialLinks from "@/components/SocialLinks";
import ShareButtons from "@/components/ShareButtons";
import { ShoppingBag, CalendarCheck } from "lucide-react";

export default function RestaurantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchRestaurant(id)
      .then(setR)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
    trackRestaurantView(id);
  }, [id]);

  const onClaim = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setClaiming(true);
    try {
      const updated = await claimRestaurant(id);
      setR(updated);
      toast.success("Listing claimed! You can now edit it.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not claim listing");
    } finally {
      setClaiming(false);
    }
  };

  const isOwner = user && r && r.owner_user_id === user.user_id;
  const isClaimedByOther = r && r.owner_user_id && (!user || r.owner_user_id !== user.user_id);

  if (loading) return <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 font-serif italic text-xl text-sand-700">Loading…</div>;
  if (err || !r) return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 text-center">
      <p className="font-serif italic text-2xl text-sand-700">Listing not found.</p>
      <Link to="/directory" className="mt-6 inline-block font-mono text-xs tracking-widest uppercase text-brand-500">← Back to directory</Link>
    </div>
  );

  return (
    <div data-testid="restaurant-detail-page" className="bg-sand-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10">
        <Link to="/directory" data-testid="back-to-directory" className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-sand-700 hover:text-brand-500 transition-colors">
          <ArrowLeft size={13} /> Back to Directory
        </Link>
      </div>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 animate-fade-up">
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-5">
            {r.category} · {r.cuisine} · {r.price_range}
          </div>
          <h1 className="font-serif font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-sand-900 flex items-start gap-3">
            <span>{r.name}</span>
            {r.verified && <BadgeCheck size={36} className="text-brand-500 shrink-0 mt-3" data-testid="detail-verified-badge" />}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sand-700">
            <span className="flex items-center gap-2"><MapPin size={15} /> {r.neighborhood}{r.city ? ` · ${r.city}` : ""}</span>
            <span className="flex items-center gap-2"><Star size={15} className="fill-ochre-400 text-ochre-400" /> {r.rating?.toFixed(1)}</span>
          </div>
          <p className="mt-8 text-xl font-light text-sand-700 leading-relaxed italic font-serif">
            {r.short_description}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            {isOwner ? (
              <>
                <span className="inline-flex items-center gap-2 bg-forest-500 text-sand-50 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase" data-testid="listing-owned-badge">
                  <BadgeCheck size={13} /> You manage this listing
                </span>
                <Link
                  to={`/directory/${r.id}/edit`}
                  data-testid="listing-edit-link"
                  className="inline-flex items-center gap-2 bg-sand-900 text-sand-50 px-5 py-3 font-mono text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-brand-500 transition-colors"
                >
                  <Pencil size={13} /> Edit Listing
                </Link>
              </>
            ) : isClaimedByOther ? (
              <span className="inline-flex items-center gap-2 border border-sand-300 text-sand-700 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase" data-testid="listing-claimed-badge">
                <ShieldCheck size={13} /> Claimed by owner
              </span>
            ) : (
              <button
                onClick={onClaim}
                disabled={claiming}
                data-testid="claim-listing-button"
                className="inline-flex items-center gap-2 bg-brand-500 text-sand-50 px-6 py-3 font-mono text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-brand-600 transition-colors active:scale-95 disabled:opacity-60"
              >
                <ShieldCheck size={14} /> {claiming ? "Claiming…" : "Claim This Listing"}
              </button>
            )}
          </div>
          {!r.owner_user_id && (
            <p className="mt-3 font-mono text-[10px] tracking-widest uppercase text-sand-600">
              Own this business? Claim it to manage your story.
            </p>
          )}

          {/* Order / Reserve (affiliate deep-links) */}
          <div className="mt-8 flex flex-wrap gap-3" data-testid="order-reserve">
            <a
              href={r.order_url || `https://www.google.com/search?q=${encodeURIComponent(`order ${r.name} ${r.city} delivery`)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="order-now-btn"
              className="inline-flex items-center gap-2 bg-brand-500 text-sand-50 px-6 py-3 font-mono text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-brand-600 transition-colors"
            >
              <ShoppingBag size={15} /> Order Now
            </a>
            <a
              href={r.reserve_url || `https://www.opentable.com/s?term=${encodeURIComponent(r.name)}&covers=2`}
              target="_blank"
              rel="noreferrer"
              data-testid="reserve-btn"
              className="inline-flex items-center gap-2 border border-sand-900 text-sand-900 px-6 py-3 font-mono text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-sand-900 hover:text-sand-50 transition-colors"
            >
              <CalendarCheck size={15} /> Reserve a Table
            </a>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
            <SocialLinks item={r} />
            <ShareButtons url={typeof window !== "undefined" ? window.location.href : ""} title={r.name} />
          </div>
        </div>
        <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "150ms" }}>
          <div className="img-zoom aspect-[4/5]">
            <img src={r.cover_image} alt={r.name} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-sand-200 py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-5">The Story</div>
          <p className="font-serif text-xl md:text-2xl leading-relaxed text-sand-900 drop-cap">
            {r.long_description}
          </p>

          {r.signature_dishes?.length > 0 && (
            <div className="mt-16">
              <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-6">Signature Dishes</div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
                {r.signature_dishes.map((d, i) => (
                  <li key={i} className="py-5 border-b border-sand-200 font-serif text-2xl text-sand-900 flex items-start gap-4">
                    <span className="font-mono text-sm text-brand-500 pt-2">0{i + 1}</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-20">
        <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-6">Visit</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {r.address && (
            <div className="border-t-2 border-sand-900 pt-5">
              <div className="font-mono text-[10px] tracking-widest uppercase text-sand-600 mb-2 flex items-center gap-2"><MapPin size={12} /> Address</div>
              <p className="font-serif text-lg text-sand-900">{r.address}</p>
            </div>
          )}
          {r.phone && (
            <div className="border-t-2 border-sand-900 pt-5">
              <div className="font-mono text-[10px] tracking-widest uppercase text-sand-600 mb-2 flex items-center gap-2"><Phone size={12} /> Phone</div>
              <p className="font-serif text-lg text-sand-900">{r.phone}</p>
            </div>
          )}
          {r.instagram && (
            <div className="border-t-2 border-sand-900 pt-5">
              <div className="font-mono text-[10px] tracking-widest uppercase text-sand-600 mb-2 flex items-center gap-2"><Instagram size={12} /> Instagram</div>
              <p className="font-serif text-lg text-sand-900">{r.instagram}</p>
            </div>
          )}
        </div>
      </section>

      <VideoSection entityType="restaurant" entityId={id} isOwner={isOwner} />
      <ReviewSection entityType="restaurant" entityId={id} />
    </div>
  );
}
